import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApprovalState, isPostFailed, TRASH_RETENTION_DAYS } from '@mg/shared';
import type { Campaign, Post } from '@mg/shared';
import {
  Eye,
  FileText,
  Image as ImageIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { invalidateContentQueries } from '../../lib/contentCache';
import { useThumbnailSrc } from '../../lib/useProxyImage';
import { channelLabel, displayStateLabel, displayStateTone, formatRelative, type PostDisplayState,
  failureChipState,
} from '../../lib/display';
import { cn } from '../../lib/cn';
import { hcUnderlineIdle, hcUnderlineSelected } from '../../lib/forcedColors';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { useDialogs } from '../../components/ui/DialogProvider';
import { PostThumbnail } from '../posts/PostThumbnail';
import { PlatformIcon } from '../posts/PlatformIcon';
import { postTypeIcon } from '../posts/postTypes';
import { CampaignFormModal } from './CampaignFormModal';
import {
  campaignBadgeState,
  parseStatusTab,
  SUBHEADER_CLS,
  TAB_ORDER,
  tabForState,
  type StatusTabId,
} from '../approval/statusTabs';
import { postBadgeState } from '../posts/PostsView';
import { useEffectiveApprovalType } from '../../lib/useEffectiveApprovalType';

/**
 * Can this campaign still be edited? Keyed on its OWN stored state, NOT on the displayed badge:
 * that badge is derived (in None mode it reports the least-mature POST state), so a still-DRAFT
 * campaign whose posts have all gone out displays "Published" while the API would happily accept
 * an edit. `askDelete` below gates on the same value for the same reason.
 */
function campaignReadOnly(campaign: Campaign): boolean {
  return (
    campaign.approvalState !== ApprovalState.DRAFT &&
    campaign.approvalState !== ApprovalState.REVISE
  );
}

type CampaignLayout = 'list' | 'tile';
function parseCampaignLayout(value: string | null): CampaignLayout {
  return value === 'tile' ? 'tile' : 'list';
}

/** How many post thumbnails a tile shows before collapsing the rest into a "+N more" chip. */
const MAX_THUMBS = 4;

interface CampaignItemProps {
  campaign: Campaign;
  /** The campaign's posts, oldest-created first (mapper-populated thumbnails). */
  posts: Post[];
  /** Display state — the campaign's own, or derived from its posts when approvals are off. */
  state: PostDisplayState;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * "Grouped by Campaign" view of the merged Campaigns & Posts page — the 7 shared status tabs
 * (filtered on each campaign's own `approvalState`). The List/Tile switcher is owned by the
 * PAGE so it shares a row with the view tabs. Tiles are 3×5-style
 * cards with a cover image (the first-created post that has one, else a generic icon) and a strip
 * of per-post thumbnails; the List rows match the compact campaign layout. Post thumbnails/cover +
 * the platform/type icon row come from `listPosts` grouped client-side by `campaignId` (the
 * campaign LIST endpoint carries only `postCount`). Each item links into the campaign detail page
 * and offers inline Edit/Delete. The parent owns the New Campaign modal (`onNewCampaign`).
 */
export function CampaignsView({ onNewCampaign }: { onNewCampaign: () => void }) {
  const tenantKey = useTenantKey();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { confirm, alert } = useDialogs();

  const tab = parseStatusTab(params.get('tab'));
  const layout = parseCampaignLayout(params.get('clayout'));

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['campaigns', tenantKey],
    queryFn: ({ signal }) => api.listCampaigns(signal),
    enabled: tenantKey !== '',
  });
  // Posts carry the mapper-populated thumbnailUrl/mediaKind/platform used for covers + the
  // per-post thumbnail strip + the platform/type icon row; the campaign LIST endpoint returns
  // only postCount. Shares the ['posts', tenantKey] cache with the "All Posts" tab.
  const postsQuery = useQuery({
    queryKey: ['posts', tenantKey],
    queryFn: ({ signal }) => api.listPosts(signal),
    enabled: tenantKey !== '',
  });

  const all = useMemo(() => data ?? [], [data]);

  // campaignId → its posts, oldest-created first, so posts[0]-with-an-image is the cover and the
  // thumbnail strip reads chronologically.
  const postsByCampaign = useMemo(() => {
    const map = new Map<string, Post[]>();
    for (const p of postsQuery.data ?? []) {
      const list = map.get(p.campaignId);
      if (list) list.push(p);
      else map.set(p.campaignId, [p]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    return map;
  }, [postsQuery.data]);

  // With approvals OFF a campaign has no review chain to move it off DRAFT, so it must be derived
  // from its posts — otherwise it reads "Draft" forever while every post shows Scheduled. Used for
  // the badges AND the status tabs, so a campaign can't be badged Scheduled yet filed under Draft.
  const approvalType = useEffectiveApprovalType();
  const stateOf = useMemo(() => {
    const map = new Map<string, PostDisplayState>();
    for (const c of all) {
      const posts = postsByCampaign.get(c.id) ?? [];
      // A campaign holding ANY failed post reads Failed — a release that didn't happen is the
      // thing you need to see, and it isn't part of the approval pipeline to aggregate over.
      map.set(
        c.id,
        posts.some((p) => isPostFailed(p))
          ? 'FAILED'
          : campaignBadgeState(
              c.approvalState,
              posts.map((p) => postBadgeState(p, approvalType)),
              approvalType,
            ),
      );
    }
    return map;
  }, [all, postsByCampaign, approvalType]);
  const effectiveState = (c: Campaign): PostDisplayState => stateOf.get(c.id) ?? c.approvalState;

  /**
   * Every tab a campaign belongs to — one per DISTINCT status among its posts.
   *
   * A campaign is a container, not a single piece of work: "One-Off Posts" holds drafts and
   * published posts at the same time, forever. Filing it under one aggregate status meant the
   * drafts inside it were invisible from the Draft tab, which is exactly where someone goes
   * looking for a draft. So it appears in each tab it actually has work in.
   *
   * A campaign with NO posts still has to be findable, so it falls back to its own badge state.
   */
  const tabsOf = useMemo(() => {
    const map = new Map<string, Set<StatusTabId>>();
    for (const c of all) {
      const posts = postsByCampaign.get(c.id) ?? [];
      const tabs = new Set<StatusTabId>();
      for (const p of posts) {
        // The post's CHIP state — the same thing its card shows, failure included.
        const id = tabForState(failureChipState(p) ?? postBadgeState(p, approvalType));
        if (id) tabs.add(id);
      }
      if (tabs.size === 0) {
        const own = tabForState(stateOf.get(c.id) ?? (c.approvalState as PostDisplayState));
        if (own) tabs.add(own);
      }
      map.set(c.id, tabs);
    }
    return map;
  }, [all, approvalType, postsByCampaign, stateOf]);

  const inTab = (c: Campaign, id: StatusTabId): boolean => tabsOf.get(c.id)?.has(id) ?? false;

  const items = useMemo(
    () => all.filter((c) => tabsOf.get(c.id)?.has(tab) ?? false),
    [all, tab, tabsOf],
  );
  const tabCount = (id: StatusTabId): number => all.filter((c) => inTab(c, id)).length;

  const [editing, setEditing] = useState<Campaign | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.deleteCampaign(id),
    onSuccess: () => invalidateContentQueries(queryClient, tenantKey),
    onError: (err) =>
      void alert({
        title: 'Could not delete campaign',
        message: err instanceof ApiRequestError ? err.message : 'Please try again.',
      }),
  });

  async function askDelete(c: Campaign) {
    // Any state can be deleted EXCEPT a Published campaign. Keyed on the campaign's OWN stored
    // state so this matches the server gate exactly — the displayed badge can be derived (NONE
    // mode, or Failed) and gating on that would offer deletes the API then refuses.
    if (c.approvalState === ApprovalState.PUBLISHED) {
      await alert({
        title: 'Can’t delete this campaign',
        message: `“${c.name}” has been published, so it can’t be deleted.`,
      });
      return;
    }
    if (
      await confirm({
        title: 'Delete campaign',
        message: (
          <>
            Delete “{c.name}”? Its posts go to the Trash too — you can restore them within{' '}
            {TRASH_RETENTION_DAYS} days.
          </>
        ),
        confirmLabel: 'Delete',
        danger: true,
      })
    )
      del.mutate(c.id);
  }

  return (
    <>
      <div className="mb-4">
        <p className={SUBHEADER_CLS}>Status</p>
        <div className="flex flex-wrap gap-1 border-b border-line-soft">
          {TAB_ORDER.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set('tab', t.id);
                  setParams(next, { replace: true });
                }}
                className={cn(
                  'focus-ring -mb-px rounded-t-md px-3.5 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'border-b-2 border-action text-ink'
                    : 'border-b-2 border-transparent text-ink-muted hover:text-ink',
                  active ? hcUnderlineSelected : hcUnderlineIdle,
                )}
                aria-current={active ? 'page' : undefined}
              >
                {t.label} ({tabCount(t.id)})
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <LoadingState label="Loading campaigns…" />
      ) : isError ? (
        <ErrorState
          message={error instanceof ApiRequestError ? error.message : 'Could not load campaigns.'}
          onRetry={() => void refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No campaigns here"
          description="Create a campaign to group posts, or switch tabs to see campaigns in other states."
          action={
            <Button onClick={onNewCampaign}>
              <Plus className="h-4 w-4" aria-hidden />
              New Campaign
            </Button>
          }
        />
      ) : layout === 'tile' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((campaign) => (
            <CampaignTile
              key={campaign.id}
              campaign={campaign}
              posts={postsByCampaign.get(campaign.id) ?? []}
              state={effectiveState(campaign)}
              onEdit={() => setEditing(campaign)}
              onDelete={() => void askDelete(campaign)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-card">
          {items.map((campaign) => (
            <CampaignRow
              key={campaign.id}
              campaign={campaign}
              posts={postsByCampaign.get(campaign.id) ?? []}
              state={effectiveState(campaign)}
              onEdit={() => setEditing(campaign)}
              onDelete={() => void askDelete(campaign)}
            />
          ))}
        </div>
      )}

      <CampaignFormModal
        open={editing !== null}
        campaign={editing}
        // A campaign the API won't accept edits for opens as a read-only detail view, so the
        // eye/"View" affordance is telling the truth rather than offering a Save that must fail.
        readOnly={editing != null && campaignReadOnly(editing)}
        onClose={() => setEditing(null)}
        onSaved={() => setEditing(null)}
      />
    </>
  );
}

/** The campaign's cover: the first-created post that has an image (else a generic icon). */
function CampaignCover({ posts, iconClassName }: { posts: Post[]; iconClassName?: string }) {
  const cover = posts.find((p) => p.thumbnailUrl) ?? null;
  const src = useThumbnailSrc(cover?.thumbnailUrl ?? null);
  const [errored, setErrored] = useState(false);
  // Clear a stale load failure when the resolved image changes (e.g. a refetch mints a fresh SAS
  // URL, or the cover moves to a different post) — otherwise the latch keeps showing the placeholder.
  useEffect(() => setErrored(false), [src]);
  if (src && !errored) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setErrored(true)}
        // bg-white shows only through transparent pixels: a dark-on-transparent cover stays
        // legible in the dark theme.
        className="h-full w-full bg-white object-cover"
      />
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center bg-chip text-ink-ghost">
      <ImageIcon className={cn('h-8 w-8', iconClassName)} aria-hidden />
    </div>
  );
}

/** A deduped row of platform (social) / post-type (non-social) glyphs spanning a campaign's posts. */
function CampaignIconRow({ posts, max = 6 }: { posts: Post[]; max?: number }) {
  const seen = new Set<string>();
  const entries: ReactNode[] = [];
  for (const p of posts) {
    if (entries.length >= max) break;
    if (p.platform) {
      const key = `pf:${p.platform}`;
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(<PlatformIcon key={key} platform={p.platform} size="sm" />);
    } else {
      const key = `ch:${p.channel}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const Icon = postTypeIcon[p.channel] ?? FileText;
      entries.push(
        <span
          key={key}
          className="inline-flex h-5 w-5 items-center justify-center"
          title={channelLabel[p.channel]}
        >
          <Icon className="h-4 w-4 text-ink-faint" aria-hidden />
        </span>,
      );
    }
  }
  if (entries.length === 0) return null;
  return <div className="flex items-center gap-1">{entries}</div>;
}

/** A 3×5-style campaign card: cover image, name + status, post count + icons, and a thumbnail strip. */
function CampaignTile({ campaign, posts, state, onEdit, onDelete }: CampaignItemProps) {
  const postCount = campaign.postCount ?? posts.length;
  const failedCount = campaign.failedPostCount ?? 0;
  const shown = posts.slice(0, MAX_THUMBS);
  const extra = Math.max(0, posts.length - shown.length);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-line bg-card shadow-card transition hover:shadow-panel">
      <Link to={`/campaigns/${campaign.id}`} className="focus-ring flex flex-col">
        <div className="aspect-[5/3] w-full overflow-hidden bg-chip">
          <CampaignCover posts={posts} iconClassName="h-10 w-10" />
        </div>
        <div className="flex flex-col gap-2 p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 truncate text-base font-semibold text-ink">
              {campaign.name}
            </h3>
            <Badge tone={displayStateTone(state)} size="sm">
              {displayStateLabel(state)}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <span className="inline-flex shrink-0 items-center gap-1.5">
              <FileText className="h-4 w-4" aria-hidden />
              {postCount} post{postCount === 1 ? '' : 's'}
            </span>
            {/* A campaign completes even with these outstanding — otherwise one revoked token pins
                it at Approved forever — so the state alone would overstate what actually shipped. */}
            {failedCount > 0 && (
              <Badge tone="red" size="sm">
                {failedCount} failed
              </Badge>
            )}
            <CampaignIconRow posts={posts} />
          </div>
          {posts.length > 0 && (
            <div className="mt-1 flex items-center gap-1.5">
              {shown.map((p) => (
                <PostThumbnail
                  key={p.id}
                  mediaKind={p.mediaKind}
                  thumbnailUrl={p.thumbnailUrl}
                  className="h-11 w-11 rounded-lg border border-line-soft"
                />
              ))}
              {extra > 0 && (
                <span className="flex h-11 min-w-[2.75rem] items-center justify-center rounded-lg border border-dashed border-line-dashed px-1.5 text-xs font-medium text-ink-muted">
                  +{extra} more
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
      <div className="absolute right-2 top-2 z-10">
        <CampaignMenu campaign={campaign} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  );
}

/** A compact campaign list row: cover · name · post count · icons · status · timestamp · actions. */
function CampaignRow({ campaign, posts, state, onEdit, onDelete }: CampaignItemProps) {
  const postCount = campaign.postCount ?? posts.length;
  const failedCount = campaign.failedPostCount ?? 0;
  return (
    <div className="relative flex items-center gap-3 border-b border-line-soft px-3 py-2.5 first:rounded-t-xl last:rounded-b-xl last:border-b-0 hover:bg-hover">
      <Link
        to={`/campaigns/${campaign.id}`}
        className="focus-ring flex min-w-0 flex-1 items-center gap-3"
      >
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg">
          <CampaignCover posts={posts} iconClassName="h-5 w-5" />
        </div>
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{campaign.name}</p>
        <span className="hidden shrink-0 whitespace-nowrap text-sm text-ink-muted sm:block">
          {postCount} post{postCount === 1 ? '' : 's'}
        </span>
        {failedCount > 0 && (
          <Badge tone="red" size="sm">
            {failedCount} failed
          </Badge>
        )}
        <div className="hidden shrink-0 sm:block">
          <CampaignIconRow posts={posts} max={5} />
        </div>
        <Badge tone={displayStateTone(state)} size="sm">
          {displayStateLabel(state)}
        </Badge>
        <span className="hidden w-20 shrink-0 text-right text-xs text-ink-faint md:block">
          {formatRelative(campaign.updatedAt)}
        </span>
      </Link>
      <div className="flex shrink-0 items-center gap-0.5">
        {/* Published campaigns open read-only — an eye, not a pencil. */}
        <button
          type="button"
          onClick={onEdit}
          className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong"
          aria-label={`${campaignReadOnly(campaign) ? 'View' : 'Edit'} ${campaign.name}`}
        >
          {campaignReadOnly(campaign) ? (
            <Eye className="h-4 w-4" aria-hidden />
          ) : (
            <Pencil className="h-4 w-4" aria-hidden />
          )}
        </button>
        <CampaignMenu campaign={campaign} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  );
}

/** The ⋯ actions menu (Open / Edit / Delete) shared by tiles and rows. */
function CampaignMenu({
  campaign,
  onEdit,
  onDelete,
}: {
  campaign: Campaign;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // Close on any outside click or Escape via document listeners — NOT a full-screen `fixed`
  // catcher: each card/tile is its own positioned stacking context, so a sibling card's catcher
  // paints under this menu's button and never receives the click (two menus could stay open). A
  // document listener fires regardless of stacking, and (unlike an overlay) can't be clipped by an
  // ancestor's `overflow-hidden`.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="focus-ring rounded-md bg-card/80 p-1.5 text-ink-muted backdrop-blur hover:bg-chip hover:text-ink-strong"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${campaign.name}`}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 z-30 w-40 overflow-hidden rounded-lg border border-line bg-card py-1 shadow-panel"
        >
          <Link
            to={`/campaigns/${campaign.id}`}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-3 py-1.5 text-sm text-ink-strong hover:bg-hover"
          >
            Open
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-ink-strong hover:bg-hover"
          >
            {campaignReadOnly(campaign) ? (
              <Eye className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            )}
            {campaignReadOnly(campaign) ? 'View' : 'Edit'}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

